import axios from "axios";
import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { getUserId, getApiKey, getTenantId } from "src/utils/getters";
import type {
  NotificationSettingsResponse,
  NotificationOptionsResponse,
  UserLanguagePreferenceResponse,
} from "src/layouts/MyAccount/Notifications/NotificationInterfaces";

const API_BASE_URL = getApiBaseUrl();

// Updated fetchAllNotifications
export const fetchAllNotifications = async (lang?: string | null | ApiLang) => {
  const userId = getUserId();
  const apiKey = getApiKey();

  if (!userId || !apiKey) {
    throw new Error("Missing user_id or api_key in localStorage.");
  }

  const query = new URLSearchParams({ lang: normalizeApiLang(lang) });
  const response = await axios.post(
    `${API_BASE_URL}/all-notifications?${query.toString()}`,
    { user_id: userId },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
    }
  );

  const data = response.data;
  if (!data || !data.grouped_notifications) {
    throw new Error("Invalid response format.");
  }

  return data.grouped_notifications;
};

// -------- Notification Settings API --------

export type { NotificationSettingsResponse };

// GET /api/notifications/settings — auth resolved from Bearer token.

export const fetchNotificationSettings =
  async (lang?: string): Promise<NotificationSettingsResponse> => {
    const apiKey = getApiKey();

    if (!apiKey) {
      throw new Error("Missing api_key in localStorage.");
    }

    const query = lang ? `?lang=${lang}` : "";
    const response = await axios.get(
      `${API_BASE_URL}/notifications/settings${query}`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );

    return response.data;
  };

// GET /api/notifications/options — dropdown values (localized)
export const fetchNotificationOptions =
  async (lang?: string): Promise<NotificationOptionsResponse> => {
    const apiKey = getApiKey();

    if (!apiKey) {
      throw new Error("Missing api_key in localStorage.");
    }

    const query = lang ? `?lang=${lang}` : "";
    const response = await axios.get(
      `${API_BASE_URL}/notifications/options${query}`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );

    return response.data;
  };

// -------- User Language Preference API (email & push delivery language) --------

// GET /api/user/language
export const fetchUserLanguagePreference =
  async (): Promise<UserLanguagePreferenceResponse> => {
    const apiKey = getApiKey();

    if (!apiKey) {
      throw new Error("Missing api_key in localStorage.");
    }

    const response = await axios.get(`${API_BASE_URL}/user/language`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    return response.data;
  };

// PUT /api/user/language — accepts only "en" / "ar"
export const updateUserLanguagePreference = async (
  language: "en" | "ar"
): Promise<UserLanguagePreferenceResponse> => {
  const apiKey = getApiKey();

  if (!apiKey) {
    throw new Error("Missing api_key in localStorage.");
  }

  const response = await axios.put(
    `${API_BASE_URL}/user/language`,
    { language },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// Account settings
// -------- API: Account Display Settings (GET) --------
export const fetchAccountDisplayData = async () => {
  const userId = getUserId();
  const apiKey = getApiKey();

  if (!userId || !apiKey) {
    throw new Error("Missing user_id or api_key.");
  }

  const response = await axios.post(
    `${API_BASE_URL}/account-settings/display`,
    {
      user_id: userId,
    },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// -------- API: Dashboard Data (GET) --------
export const fetchDashboardData = async () => {
  const userId = getUserId();
  const apiKey = getApiKey();

  if (!userId || !apiKey) {
    throw new Error("Missing user_id or api_key.");
  }

  const response = await axios.post(
    `${API_BASE_URL}/dashboard-url`,
    {
      user_id: userId,
    },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// -------- API: Update Profile --------
export const updateAccountProfile = async (
  firstName: string,
  lastName: string,
  contactNumber: string,
  profileImage?: File,
  websiteUrl?: string,
  industry?: string
) => {
  const userId = getUserId();
  const apiKey = getApiKey();
  const roleId = localStorage.getItem("role_id");
  const roleIdNum = roleId ? parseInt(roleId, 10) : 0;
  const isAdmin = roleIdNum === 1;

  if (!userId || !apiKey) {
    throw new Error("Missing user_id or api_key.");
  }

  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("first_name", firstName);
  formData.append("last_name", lastName);
  formData.append("pnumber", contactNumber);

  if (profileImage) {
    formData.append("profile_image", profileImage, profileImage.name);
  }

  if (websiteUrl && isAdmin) {
    formData.append("website_url", websiteUrl);
  }

  if (industry && isAdmin) {
    formData.append("industry", industry);
  }

  const response = await axios.post(
    `${API_BASE_URL}/update-account-settings/profile?api_key=${apiKey}`,
    formData,
    {
      headers: {},
    }
  );

  return response.data;
};


// -------- API: Update Password --------
export const updateAccountPassword = async (
  oldPassword: string,
  newPassword: string
) => {
  const userId = getUserId();
  const apiKey = getApiKey();

  if (!userId || !apiKey) {
    throw new Error("Missing user_id or api_key.");
  }

  const response = await axios.post(
    `${API_BASE_URL}/update-account-settings/password`,
    {
      user_id: userId,
      old_password: oldPassword,
      new_password: newPassword,
    },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// remove logo
 
export const removeLogo = async (payload: { user_id: number }) => {
   const apiKey = localStorage.getItem("api_key");
  try {
    const response = await axios.post(
      `${API_BASE_URL}/remove-logo?api_key=${apiKey}`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error: any) {
    console.error("Remove logo error:", error);
    throw error.response?.data || { message: "Something went wrong" };
  }
};

// -------- API: Tenant Display Currency --------
export interface TenantCurrencyResponse {
  status: boolean;
  tenant_id: number;
  currency_code: string;
  supported_currencies: string[];
  message: string;
}

export const fetchTenantCurrency = async (
  lang?: string | null | ApiLang
): Promise<TenantCurrencyResponse> => {
  const apiKey = getApiKey();
  const query = new URLSearchParams({
    tenant_id: getTenantId(),
    lang: normalizeApiLang(lang),
  });

  const response = await axios.get<TenantCurrencyResponse>(
    `${API_BASE_URL}/tenant/currency?${query.toString()}`,
    { headers: { Authorization: `Bearer ${apiKey}` } }
  );

  return response.data;
};

export const updateTenantCurrency = async (
  currencyCode: string,
  lang?: string | null | ApiLang
): Promise<TenantCurrencyResponse> => {
  const apiKey = getApiKey();
  const query = new URLSearchParams({ lang: normalizeApiLang(lang) });

  const response = await axios.put<TenantCurrencyResponse>(
    `${API_BASE_URL}/tenant/currency?${query.toString()}`,
    { tenant_id: Number(getTenantId()), currency_code: currencyCode },
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};
